/**
 * Proposal quality lint engine (pure, framework-free).
 *
 * Runs in real time as the freelancer types a cover letter so they see exactly
 * why a draft is weak BEFORE spending Connects/credits to submit it. All rules
 * are local — no network, no LLM call — so the gate stays instant and free.
 *
 * Also exposes computeClientResponsiveness() which derives a "how likely is
 * this client to actually reply?" signal from whatever job/client metadata the
 * page already has. We deliberately return `unknown` when data is missing
 * rather than fake confidence; a misleading high score would cost the
 * freelancer real money.
 */

const STOP_WORDS = new Set([
  'the', 'and', 'for', 'with', 'that', 'this', 'from', 'you', 'your', 'our',
  'are', 'will', 'have', 'has', 'was', 'were', 'been', 'about', 'into', 'over',
  'than', 'then', 'there', 'their', 'they', 'them', 'its', 'but', 'not', 'can',
  'any', 'all', 'who', 'what', 'when', 'where', 'why', 'how', 'job', 'project',
  'work', 'looking', 'need', 'want', 'like', 'use', 'using', 'well', 'good',
  'great', 'make', 'also', 'just', 'very', 'really', 'please', 'thanks',
  'would', 'should', 'could', 'must', 'may', 'might', 'shall', 'one', 'two',
  'three', 'per', 'out', 'off', 'upon', 'such', 'some', 'more', 'most',
  'other', 'each', 'every', 'someone', 'anyone',
]);

const GENERIC_OPENERS = [
  /^hi\b/i,
  /^hello\b/i,
  /^hey\b/i,
  /^dear\s+(hiring\s+manager|sir|madam|client)/i,
  /^to\s+whom\s+it\s+may\s+concern/i,
  /^greetings\b/i,
];

const CTA_WORDS = [
  "let's", 'lets ', 'schedule', 'call', 'chat', 'discuss', 'hop on',
  'next step', 'jump on', 'book a time', 'connect', 'walk you through',
  'happy to share', 'send over', 'when works', 'free to chat', 'available to',
];

const PLACEHOLDER_PATTERNS = [
  /\[[^\]]{1,40}\]/,         // [name], [client], [your project]
  /\{\{[^}]+\}\}/,           // {{name}}
  /\btodo\b/i,
  /\blorem ipsum\b/i,
  /\bxxx+\b/i,
];

const tokenize = (text) =>
  (text || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

const extractKeywords = (text, limit = 15) => {
  const counts = new Map();
  for (const w of tokenize(text)) {
    if (w.length < 4) continue;
    if (STOP_WORDS.has(w)) continue;
    counts.set(w, (counts.get(w) || 0) + 1);
  }
  return [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([w]) => w);
};

const wordCountOf = (text) => tokenize(text).length;

const passCheck = (id, label) => ({ id, label, status: 'pass' });
const warnCheck = (id, label, tip) => ({ id, label, status: 'warn', tip });
const failCheck = (id, label, tip) => ({ id, label, status: 'fail', tip });

/**
 * Run the lint engine against a proposal draft.
 *
 * @param {object} params
 * @param {string} params.coverLetter - The freelancer's draft cover letter.
 * @param {object} [params.job]       - The job context (title, description, skills).
 * @returns {{ score: number, checks: Array, wordCount: number, tips: string[] }}
 */
export const runProposalLint = ({ coverLetter = '', job = {} } = {}) => {
  const text = (coverLetter || '').trim();
  const lower = text.toLowerCase();
  const wordCount = wordCountOf(text);
  const checks = [];

  // 1. Length
  if (wordCount === 0) {
    checks.push(failCheck('length', 'Add a cover letter', 'Clients skip empty proposals automatically.'));
  } else if (wordCount < 60) {
    checks.push(warnCheck('length', `Only ${wordCount} words — too short`, 'Aim for 100–300 words. Short proposals read as low effort.'));
  } else if (wordCount > 500) {
    checks.push(warnCheck('length', `${wordCount} words — too long`, "Clients skim. Cut to ~300 words by removing anything that isn't about their project."));
  } else {
    checks.push(passCheck('length', `${wordCount}-word draft — good length`));
  }

  // 2. Generic opener
  const firstLine = (text.split(/\n|\./)[0] || '').trim();
  if (firstLine && GENERIC_OPENERS.some((re) => re.test(firstLine))) {
    checks.push(warnCheck(
      'opener',
      'Generic opener detected',
      'Replace "Hi / Dear hiring manager" with something specific to their project in the first sentence.'
    ));
  } else if (wordCount > 0) {
    checks.push(passCheck('opener', 'Opener avoids generic greetings'));
  }

  // 3. References client's goal (job title/description keywords)
  const jobText = [job?.title, job?.description].filter(Boolean).join(' ');
  const jobKeywords = extractKeywords(jobText, 15);
  if (jobKeywords.length > 0 && wordCount > 0) {
    const matchedGoalWords = jobKeywords.filter((kw) => lower.includes(kw));
    if (matchedGoalWords.length === 0) {
      checks.push(failCheck(
        'goal',
        "Doesn't reference the client's goal",
        `Mention something specific from the brief — e.g. "${jobKeywords.slice(0, 3).join('", "')}".`
      ));
    } else if (matchedGoalWords.length < 2) {
      checks.push(warnCheck(
        'goal',
        'Only 1 keyword from the job referenced',
        "Echo the client's own language — name the specific outcome they're after."
      ));
    } else {
      checks.push(passCheck('goal', `References the client's goal (${matchedGoalWords.slice(0, 3).join(', ')})`));
    }
  }

  // 4. Skills coverage
  const jobSkills = (job?.skills || [])
    .map((s) => String(s || '').toLowerCase().trim())
    .filter(Boolean);
  if (jobSkills.length > 0 && wordCount > 0) {
    const matchedSkills = jobSkills.filter((s) => lower.includes(s));
    const pct = matchedSkills.length / jobSkills.length;
    if (pct === 0) {
      checks.push(failCheck(
        'skills',
        'None of the required skills mentioned',
        `Show directly that you have ${jobSkills.slice(0, 3).join(', ')} experience.`
      ));
    } else if (pct < 0.4) {
      checks.push(warnCheck(
        'skills',
        `${matchedSkills.length}/${jobSkills.length} required skills mentioned`,
        'Name more of the listed skills explicitly — recruiters grep proposals.'
      ));
    } else {
      checks.push(passCheck('skills', `${matchedSkills.length}/${jobSkills.length} required skills covered`));
    }
  }

  // 5. "I"-heavy vs. client-focused
  if (wordCount >= 40) {
    const iMatches = (lower.match(/\b(i|i'?m|i've|i'?ll|my|me|mine)\b/g) || []).length;
    const youMatches = (lower.match(/\b(you|your|yours|you're|you'?ll)\b/g) || []).length;
    if (iMatches > 0 && youMatches === 0) {
      checks.push(warnCheck('focus', 'Entirely self-focused', 'Use "you/your" at least as often as "I/my". The proposal is about the client.'));
    } else if (iMatches > 3 * Math.max(1, youMatches)) {
      checks.push(warnCheck('focus', 'Too self-focused', `${iMatches} mentions of yourself vs. ${youMatches} of the client. Flip the ratio.`));
    } else {
      checks.push(passCheck('focus', 'Balanced between you and the client'));
    }
  }

  // 6. Question asked
  if (wordCount >= 60) {
    if (text.includes('?')) {
      checks.push(passCheck('question', 'Includes a question for the client'));
    } else {
      checks.push(warnCheck('question', 'No question for the client', 'One thoughtful question proves you read the brief and invites a reply.'));
    }
  }

  // 7. Clear next-step CTA
  if (wordCount >= 80) {
    const hasCTA = CTA_WORDS.some((cta) => lower.includes(cta));
    if (hasCTA) {
      checks.push(passCheck('cta', 'Closes with a clear next step'));
    } else {
      checks.push(warnCheck('cta', 'No clear next step', 'End with a specific invitation: "Open to a 15-min call Thursday?"'));
    }
  }

  // 8. Placeholder leftovers
  if (PLACEHOLDER_PATTERNS.some((re) => re.test(text))) {
    checks.push(failCheck(
      'placeholder',
      'Unfilled placeholder detected',
      'Remove template markers like [name], {{client}}, TODO, or lorem ipsum before sending.'
    ));
  }

  // Score: start at 100, deduct per fail/warn.
  let score = 100;
  for (const c of checks) {
    if (c.status === 'fail') score -= 22;
    else if (c.status === 'warn') score -= 10;
  }
  if (wordCount === 0) score = 0;
  score = Math.max(0, Math.min(100, score));

  const tips = checks
    .filter((c) => c.status !== 'pass' && c.tip)
    .map((c) => c.tip);

  return { score, checks, wordCount, tips };
};

/**
 * Derive a "how likely is this client to respond?" signal from whatever
 * client/activity metadata the job page already has.
 *
 * Returns { level, score, label, factors, responseRate }. If there's no usable
 * data we return level: 'unknown' rather than guessing — false confidence here
 * would directly cost freelancers Connects.
 */
export const computeClientResponsiveness = (job = {}) => {
  const clientInfo = job?.client_info || job?.client || {};
  const activity = job?.activity || {};
  const factors = [];

  let weightedScore = 0;
  let totalWeight = 0;

  // Explicit hire rate, if backend provides one.
  if (typeof clientInfo.hire_rate === 'number') {
    weightedScore += clientInfo.hire_rate * 100 * 2;
    totalWeight += 2;
    factors.push({ label: 'Hire rate', value: `${Math.round(clientInfo.hire_rate * 100)}%` });
  }

  // Per-job response rate: interviews / proposals from job activity.
  const interviews = Number(activity.interviews);
  const proposals = Number(activity.proposals);
  let responseRate = null;
  if (Number.isFinite(interviews) && Number.isFinite(proposals) && proposals > 0) {
    responseRate = Math.min(1, interviews / proposals);
    weightedScore += responseRate * 100 * 2;
    totalWeight += 2;
    factors.push({
      label: 'Response rate',
      value: `${interviews}/${proposals} proposals interviewed`,
    });
  }

  // Rating
  const rating = Number(clientInfo.rating);
  if (Number.isFinite(rating) && rating > 0) {
    weightedScore += (rating / 5) * 100;
    totalWeight += 1;
    factors.push({ label: 'Client rating', value: `${rating.toFixed(1)} / 5` });
  }

  // Payment verified
  if (clientInfo.payment_verified === true) {
    weightedScore += 100;
    totalWeight += 1;
    factors.push({ label: 'Payment', value: 'Verified' });
  } else if (clientInfo.payment_verified === false) {
    totalWeight += 1;
    factors.push({ label: 'Payment', value: 'Not verified' });
  }

  // Hiring history signal: spend per posting.
  const jobsPosted = Number(clientInfo.jobs_posted);
  const totalSpentRaw = clientInfo.total_spent ?? clientInfo.spent ?? null;
  const totalSpent =
    totalSpentRaw == null
      ? NaN
      : Number(String(totalSpentRaw).replace(/[^0-9.]/g, ''));
  if (Number.isFinite(jobsPosted) && jobsPosted > 0 && Number.isFinite(totalSpent) && totalSpent > 0) {
    const spendPerPost = totalSpent / jobsPosted;
    // Cap at ~$2k/post for a full score; scales linearly below.
    const normalized = Math.max(0, Math.min(1, spendPerPost / 2000));
    weightedScore += normalized * 100;
    totalWeight += 1;
    factors.push({ label: 'Avg spend per job', value: `$${Math.round(spendPerPost).toLocaleString()}` });
  }

  if (totalWeight === 0) {
    return {
      level: 'unknown',
      score: null,
      label: 'Not enough data',
      factors: [],
      responseRate: null,
    };
  }

  const normalized = Math.round(weightedScore / totalWeight);
  let level;
  let label;
  if (normalized >= 75) {
    level = 'high';
    label = 'Highly responsive';
  } else if (normalized >= 50) {
    level = 'medium';
    label = 'Responsive';
  } else if (normalized >= 25) {
    level = 'low';
    label = 'Low responsiveness';
  } else {
    level = 'low';
    label = 'Rarely responds';
  }

  return { level, score: normalized, label, factors, responseRate };
};
